import React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ThemeSelectorProps {
    value: 'light' | 'dark' | 'system';
    onChange: (theme: 'light' | 'dark' | 'system') => void;
    className?: string;
}

const themeOptions = [
    {
        value: 'light' as const,
        label: 'Light',
        icon: Sun,
        gradient: 'from-amber-400 to-orange-500',
        glow: 'rgba(251, 191, 36, 0.4)',
    },
    {
        value: 'dark' as const,
        label: 'Dark',
        icon: Moon,
        gradient: 'from-indigo-500 to-purple-600',
        glow: 'rgba(99, 102, 241, 0.4)',
    },
    {
        value: 'system' as const,
        label: 'System',
        icon: Monitor,
        gradient: 'from-slate-400 to-slate-600',
        glow: 'rgba(100, 116, 139, 0.4)',
    },
];

export function ThemeSelector({ value, onChange, className }: ThemeSelectorProps) {
    return (
        <div className={cn('grid grid-cols-3 gap-3', className)}>
            {themeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = value === option.value;

                return (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => onChange(option.value)}
                        className={cn(
                            'relative group flex flex-col items-center justify-center p-5 rounded-xl transition-all duration-300 cursor-pointer overflow-hidden',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#2DC0C0]',
                            isSelected
                                ? 'bg-gradient-to-br from-[#1E3A5F] to-[#2DC0C0] shadow-lg shadow-[#2DC0C0]/25 scale-[1.02]'
                                : 'bg-white dark:bg-slate-800/80 border-2 border-slate-200 dark:border-slate-700 hover:border-[#2DC0C0]/50 dark:hover:border-[#2DC0C0]/50 hover:shadow-md'
                        )}
                        style={{
                            boxShadow: isSelected ? `0 8px 32px ${option.glow}` : undefined,
                        }}
                    >
                        {/* Background glow effect for selected state */}
                        {isSelected && (
                            <div
                                className="absolute inset-0 opacity-20"
                                style={{
                                    background: `radial-gradient(circle at 50% 0%, ${option.glow}, transparent 70%)`,
                                }}
                            />
                        )}

                        {/* Icon container with gradient */}
                        <div
                            className={cn(
                                'relative z-10 p-2.5 rounded-full mb-2 transition-all duration-300',
                                isSelected
                                    ? 'bg-white/20 backdrop-blur-sm'
                                    : `bg-gradient-to-br ${option.gradient} opacity-80 group-hover:opacity-100 group-hover:scale-110`
                            )}
                        >
                            <Icon
                                className={cn(
                                    'h-5 w-5 transition-transform duration-300',
                                    isSelected
                                        ? 'text-white'
                                        : 'text-white'
                                )}
                            />
                        </div>

                        {/* Label */}
                        <span
                            className={cn(
                                'relative z-10 text-sm font-semibold tracking-wide transition-all duration-300',
                                isSelected
                                    ? 'text-white'
                                    : 'text-slate-700 dark:text-slate-200 group-hover:text-[#1E3A5F] dark:group-hover:text-[#2DC0C0]'
                            )}
                        >
                            {option.label}
                        </span>

                        {/* Selection indicator dot */}
                        <div
                            className={cn(
                                'absolute bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full transition-all duration-300',
                                isSelected
                                    ? 'bg-white scale-100 opacity-100'
                                    : 'bg-[#2DC0C0] scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-50'
                            )}
                        />

                        {/* Selection checkmark */}
                        {isSelected && (
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                                <svg
                                    className="w-3 h-3 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={3}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

export default ThemeSelector;
